import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import type { BillingPrice, BillingProduct } from '../types';

function moneyToLabel(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

function getRecurringStripePrice(product: BillingProduct): BillingPrice | undefined {
  return product.prices.find((price) =>
    price.provider === 'stripe'
    && price.stripePriceId
    && price.interval
    && price.interval !== 'one_time',
  );
}

function moduleLabel(moduleKey: string): string {
  if (moduleKey === 'web_onboarding') return 'Web subscription';
  if (moduleKey === 'ios_sdk') return 'iOS subscription';
  return 'Subscription';
}

export interface SubscriptionProductsListProps {
  products: BillingProduct[];
  loading?: boolean;
  busyProductId?: string | null;
  actionLabel?: string;
  activeProductIds?: string[];
  onSubscribe: (productId: string) => void;
}

export default function SubscriptionProductsList({
  products,
  loading = false,
  busyProductId = null,
  actionLabel = 'Subscribe',
  activeProductIds = [],
  onSubscribe,
}: SubscriptionProductsListProps): React.ReactElement {
  if (loading) {
    return (
      <Box sx={{ py: 1 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (products.length === 0) {
    return <Alert severity="warning">No self-serve subscription products are configured.</Alert>;
  }

  const activeProductSet = new Set(activeProductIds);

  return (
    <Stack spacing={1}>
      {products.map((product) => {
        const price = getRecurringStripePrice(product);
        const priceLabel = price
          ? `${moneyToLabel(price.amountMinor, price.currency)} / ${price.interval}`
          : 'Stripe checkout not configured';
        const busy = busyProductId === product.id;
        const active = activeProductSet.has(product.id);
        const canCheckout = Boolean(price?.stripePriceId);

        return (
          <Box
            key={product.id}
            data-testid={`billing-subscription-product-${product.id}`}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography fontWeight={700}>{product.name}</Typography>
                  <Chip size="small" label={moduleLabel(product.module)} variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {product.description || product.capabilities.join(', ')}
                </Typography>
              </Box>
              <Chip size="small" label={priceLabel} color={canCheckout ? 'primary' : 'default'} variant="outlined" />
              <Button
                variant="contained"
                startIcon={active ? <CheckCircleIcon /> : <ShoppingCartIcon />}
                data-testid={product.id === 'segmently_impressions' ? 'billing-paywall-subscribe' : undefined}
                disabled={active || !canCheckout || busy}
                onClick={() => onSubscribe(product.id)}
              >
                {active ? 'Active' : busy ? 'Redirecting...' : actionLabel}
              </Button>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
